import { Dialog as HeadlessDialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { CSSProperties, Dispatch, ReactNode, SetStateAction, useEffect, useRef } from 'react';

import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon';
import classNames from 'classnames';

import { ButtonType } from '../buttons/ButtonType';
import { Card } from '../layouts/Card';
import { Size } from '../../../constants';
import { ThemeBackground } from '../../../context/theme/ThemeConstantsUpdated';

const { backdropBackground } = ThemeBackground;

export const Dialog = (props: Props) => {
  const {
    header,
    isOpen,
    toggle,
    footer,
    size,
    media,
    removeToggleButton,
    children,
    isFullscreen,
    isStatic,
    zIndex,
    preventBodyScroll = true,
  } = props;

  const onClose = () => {
    if (!isStatic) toggle(false);
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!preventBodyScroll || !isOpen) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const prevent = (e: WheelEvent | TouchEvent) => {
      if (el.scrollHeight > el.clientHeight) return; // allow scrolling inside the dialog itself
      e.preventDefault();
    };
    el.addEventListener('wheel', prevent, { passive: false });
    el.addEventListener('touchmove', prevent, { passive: false });
    return () => {
      el.removeEventListener('wheel', prevent);
      el.removeEventListener('touchmove', prevent);
    };
  }, [isOpen, preventBodyScroll]);

  const dialogZIndex = zIndex ?? 54;
  const dialogStyle: CSSProperties = { zIndex: dialogZIndex };

  return (
    <Transition show={!!isOpen}>
      <HeadlessDialog className="relative" style={dialogStyle} onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames(backdropBackground, 'fixed inset-0 bg-opacity-30 backdrop-blur transition-opacity')}
          />
        </TransitionChild>

        <div ref={scrollContainerRef} className="fixed inset-0 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={classNames(
                  'relative w-full transform overflow-hidden rounded-2xl shadow-xl transition-all sm:my-8',
                  { 'fullscreen-dialog': isFullscreen },
                  {
                    'sm:max-w-sm': size === 'sm',
                    'sm:max-w-lg': size == null,
                    'sm:max-w-2xl': size === 'lg',
                    'sm:max-w-5xl': size === 'xl',
                  }
                )}
              >
                <Card
                  headerActions={
                    removeToggleButton
                      ? undefined
                      : [{ Icon: XMarkIcon, onClick: () => toggle((prev) => !prev), buttonType: ButtonType.Danger }]
                  }
                  header={header}
                  footer={footer}
                  media={media}
                  className={classNames({ 'fullscreen-dialog': isFullscreen })}
                  forceOverflowHidden
                >
                  {children}
                </Card>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};

type Props = {
  dialogClassName?: string;
  header?: ReactNode;
  isOpen?: boolean;
  toggle: Dispatch<SetStateAction<boolean | undefined>>;
  children: ReactNode;
  footer?: ReactNode;
  size?: Size.sm | Size.lg | Size.xl | false;
  media?: ReactNode;
  removeToggleButton?: boolean;
  isFullscreen?: boolean;
  isStatic?: boolean;
  zIndex?: number;
  preventBodyScroll?: boolean;
};
